import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Card, Input, Localized } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";

const empty = () => ({
  slug: "",
  name: { tr: "", en: "", ar: "" },
  title: { tr: "", en: "", ar: "" },
  bio: { tr: "", en: "", ar: "" },
  photo: "",
  department: "",
  email: "",
  phone: "",
  linkedin_url: "",
  instagram_url: "",
  facebook_url: "",
  twitter_url: "",
  youtube_url: "",
  tiktok_url: "",
  website_url: "",
  whatsapp: "",
  order: 0,
  is_leader: false,
  status: "published",
});

const AdminTeamPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/team").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ new: true }); setForm(empty()); };
  const startEdit = (row) => { setEditing(row); setForm({ ...empty(), ...row }); };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      if (editing?.new) await api.post("/admin/team", form);
      else await api.put(`/admin/team/${editing.id}`, form);
      toast.success("Kaydedildi");
      cancel(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`${row.name?.tr || row.slug} silinsin mi?`)) return;
    try { await api.delete(`/admin/team/${row.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-team-page">
      <AdminHeader
        title="Ekibimiz"
        subtitle="Kurumsal Yönetim sayfasında görüntülenecek ekip üyelerini yönetin. is_leader=true olanlar üstteki kurul grid&apos;ine, diğerleri alttaki tam liste bölümüne düşer."
        actions={<Btn onClick={startNew} data-testid="new-team-btn"><Plus size={14} strokeWidth={1.5} /> Yeni Üye</Btn>}
      />

      <DataTable
        rows={rows}
        columns={[
          { key: "photo", label: "", render: (r) => r.photo ? <img src={r.photo.startsWith("http") ? r.photo : `${process.env.REACT_APP_BACKEND_URL}${r.photo}`} alt="" className="w-10 h-10 object-cover rounded-full" /> : <span className="w-10 h-10 inline-flex rounded-full bg-brand-navy/10 text-brand-navy items-center justify-center text-xs">?</span> },
          { key: "name.tr", label: "Ad", render: (r) => <div className="font-medium text-brand-navy">{r.name?.tr}</div> },
          { key: "title.tr", label: "Ünvan", render: (r) => <span className="text-sm text-brand-medium">{r.title?.tr}</span> },
          { key: "department", label: "Departman" },
          { key: "is_leader", label: "Yönetim", render: (r) => r.is_leader ? <span className="text-xs px-2 py-0.5 bg-brand-primary/10 text-brand-primary">Kurul</span> : <span className="text-xs text-brand-medium">—</span> },
          { key: "order", label: "Sıra" },
          {
            key: "actions", label: "", render: (r) => (
              <div className="flex items-center gap-1">
                <button onClick={() => startEdit(r)} className="text-xs px-3 py-1.5 border border-black/10 hover:border-brand-navy" data-testid={`edit-team-${r.slug}`}>Düzenle</button>
                <button onClick={() => remove(r)} className="text-red-600 hover:text-red-700 p-2" data-testid={`delete-team-${r.slug}`}><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
            ),
          },
        ]}
      />

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={cancel} />
          <form onSubmit={save} className="relative bg-white w-full max-w-3xl max-h-[92vh] shadow-2xl overflow-y-auto">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary font-medium">Ekip</div>
                <div className="font-serif text-lg text-brand-navy">{editing?.new ? "Yeni Ekip Üyesi" : form.name?.tr}</div>
              </div>
              <button type="button" onClick={cancel} className="text-brand-medium hover:text-brand-navy text-xl">×</button>
            </div>
            <div className="p-6 space-y-5">
              <Card title="Kimlik">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Slug (eşsiz)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="ornek: mehmet-yilmaz" />
                  <Input label="Departman" value={form.department} onChange={(v) => setForm({ ...form, department: v })} placeholder="Satış / Pazarlama / Yönetim" />
                </div>
                <Localized label="Ad Soyad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <Localized label="Ünvan" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <Localized label="Biyografi" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} textarea />
              </Card>

              <Card title="Fotoğraf & Video">
                <ImageUpload label="Portre (durağan görsel)" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} />
                <div className="mt-4 pt-4 border-t border-black/5 space-y-3">
                  <ImageUpload
                    kind="video"
                    label="Hover Video (opsiyonel — hover'da oynayan sessiz kısa döngü)"
                    value={form.hover_video || ""}
                    onChange={(v) => setForm({ ...form, hover_video: v })}
                    testId="team-hover-video"
                  />
                  <ImageUpload
                    label="Video için Poster Görseli (video yüklenmeden gösterilir, opsiyonel)"
                    value={form.hover_video_poster || ""}
                    onChange={(v) => setForm({ ...form, hover_video_poster: v })}
                  />
                  <div className="text-xs text-brand-medium bg-brand-light/60 p-3 border-l-2 border-brand-primary/40">
                    💡 <strong>Nasıl çalışır?</strong> Video yüklerseniz ziyaretçi kartın üzerine geldiğinde
                    otomatik/sessiz oynar. Yüklemezseniz sadece portre gösterilir. <strong>Öneriler:</strong> 3-8 sn döngü,
                    720p altı MP4, &lt;3&nbsp;MB. Cloudflare&apos;a otomatik yüklenir.
                  </div>
                </div>
              </Card>

              <Card title="İletişim">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input label="E-posta" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="ornek@firatconstruction.com" />
                  <Input label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+90 555 000 00 00" />
                  <Input label="WhatsApp (telefon)" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} placeholder="905550000000 (uluslararası)" />
                </div>
              </Card>

              <Card title="Sosyal Medya (yalnızca doldurulan alanlar site&apos;de gözükür)">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="LinkedIn" value={form.linkedin_url} onChange={(v) => setForm({ ...form, linkedin_url: v })} placeholder="https://linkedin.com/in/username" />
                  <Input label="Instagram" value={form.instagram_url} onChange={(v) => setForm({ ...form, instagram_url: v })} placeholder="https://instagram.com/username" />
                  <Input label="Facebook" value={form.facebook_url} onChange={(v) => setForm({ ...form, facebook_url: v })} placeholder="https://facebook.com/username" />
                  <Input label="X (Twitter)" value={form.twitter_url} onChange={(v) => setForm({ ...form, twitter_url: v })} placeholder="https://x.com/username" />
                  <Input label="YouTube" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} placeholder="https://youtube.com/@channel" />
                  <Input label="TikTok" value={form.tiktok_url} onChange={(v) => setForm({ ...form, tiktok_url: v })} placeholder="https://tiktok.com/@username" />
                  <Input label="Web Sitesi" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} placeholder="https://ornek.com" />
                </div>
              </Card>

              <Card title="Gösterim">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input label="Sıra" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
                  <label className="flex items-center gap-2 text-sm mt-6">
                    <input type="checkbox" checked={!!form.is_leader} onChange={(e) => setForm({ ...form, is_leader: e.target.checked })} />
                    <span>Yönetim Kurulu (üst grid&apos;de göster)</span>
                  </label>
                  <div>
                    <div className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5">Durum</div>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-black/10 px-3 py-2 text-sm">
                      <option value="published">Yayında</option>
                      <option value="draft">Taslak</option>
                    </select>
                  </div>
                </div>
              </Card>
            </div>
            <div className="sticky bottom-0 px-6 py-4 bg-brand-light border-t border-black/10 flex items-center justify-end gap-2">
              <button type="button" onClick={cancel} className="text-xs uppercase tracking-[0.2em] text-brand-medium px-4 py-2">İptal</button>
              <Btn type="submit" disabled={busy} data-testid="team-save">{busy ? "..." : "Kaydet"}</Btn>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default AdminTeamPage;
